/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Alert,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  useTheme,
} from '@mui/material';
import { RefreshCw, Save, Check, Maximize2, X } from 'lucide-react';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import { EditorView } from '@codemirror/view';
import { validatePromptTemplate } from '@shared/promptTemplate';
import {
  extractTemplateParams,
  getParamAvailability,
  getAllAvailableParams,
} from '../utils/paramUtils';
import { createTemplateDecorationExtension } from '../extensions/templateDecorations';

interface PromptTextAreaProps {
  content: string;
  originalContent: string;
  requiredParams?: string[]; // Optional - kept for backward compatibility but not used
  availableParams?: Record<string, string | number | undefined>;
  onChange: (content: string) => void;
  onReset: () => void;
  onSave: () => void;
  onRun: () => void;
  isRunning?: boolean;
  hasValidationError?: boolean;
  onOpenSyntaxGuide?: () => void;
}

export const PromptTextArea: React.FC<PromptTextAreaProps> = ({
  content,
  originalContent,
  requiredParams,
  availableParams = {},
  onChange,
  onReset,
  onSave,
  onRun,
  isRunning = false,
  hasValidationError = false,
  onOpenSyntaxGuide,
}) => {
  const theme = useTheme();
  const [isModified, setIsModified] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = React.useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dialogEditorRef = React.useRef<any>(null);

  React.useEffect(() => {
    setIsModified(content !== originalContent);
  }, [content, originalContent]);

  // Validate template on content change
  useEffect(() => {
    const validation = validatePromptTemplate(content);
    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid format');
    } else {
      setValidationError(null);
    }
  }, [content]);

  // Extract parameters from template and check for missing values
  const templateParams = extractTemplateParams(content);
  const missingParams = templateParams.filter(param => {
    const { available } = getParamAvailability(param, availableParams);
    return !available;
  });

  const canRun =
    !hasValidationError && !validationError && missingParams.length === 0;
  const canSave = isModified && content.trim().length > 0 && !validationError;

  const handleParamClick = (param: string) => {
    // Insert parameter at cursor position or end of content
    const paramText = `{{${param}}}`;

    // If we have editor view access, insert at cursor
    if (editorRef.current?.view) {
      const view = editorRef.current.view as EditorView;
      const pos = view.state.selection.main.head;
      view.dispatch({
        changes: { from: pos, insert: paramText },
        selection: { anchor: pos + paramText.length },
      });
      view.focus();
    } else {
      // Fallback: append to end
      onChange(content + paramText);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleInsertParam = (paramName: string) => {
    handleParamClick(paramName);
    handleMenuClose();
  };

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (isModified) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmed) return;
    }
    setIsDialogOpen(false);
  };

  const handleDialogBackdropClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    // Prevent closing on backdrop click if there are unsaved changes
    if (isModified) {
      event.stopPropagation();
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (confirmed) {
        setIsDialogOpen(false);
      }
    }
  };

  // Memoize CodeMirror extensions to prevent unnecessary recreations
  const extensions = useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return [
      markdown(),
      createTemplateDecorationExtension(availableParams, isDark),
      EditorView.lineWrapping, // Enable line wrapping
      EditorView.theme({
        '&': {
          fontSize: '14px',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
        },
        '&.cm-focused': {
          outline: '2px solid #1976d2',
          outlineOffset: '-2px',
        },
        '.cm-content': {
          padding: '14px',
          minHeight: '200px',
          fontFamily: 'monospace',
        },
        '.cm-line': {
          padding: '0 2px',
        },
      }),
    ];
  }, [availableParams, theme.palette.mode]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Parameter Display - Auto-extracted from template */}
      {templateParams.length > 0 && (
        <Box
          sx={{
            marginBottom: 0,
            padding: 1,
            backgroundColor: 'transparent',
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 'bold', marginRight: 1 }}
            >
              Params:
            </Typography>
            {[...templateParams].sort().map(param => {
              const { available, value } = getParamAvailability(
                param,
                availableParams
              );

              return (
                <Tooltip
                  key={param}
                  title={available ? `Value: ${value}` : 'Not available'}
                  arrow
                >
                  <Chip
                    label={param}
                    size="small"
                    variant="filled"
                    color={available ? 'default' : 'error'}
                    sx={{
                      fontSize: '0.7rem',
                      height: '18px',
                      fontWeight: '600',
                      borderRadius: '4px',
                    }}
                  />
                </Tooltip>
              );
            })}

            {/* "+" Chip for parameter insertion */}
            <Tooltip title="Insert parameter" arrow>
              <Chip
                label="+"
                size="small"
                variant="outlined"
                onClick={handleMenuOpen}
                sx={{
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                  },
                }}
              />
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Parameter Insertion Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
          },
        }}
      >
        {getAllAvailableParams(availableParams, templateParams).map(param => (
          <MenuItem
            key={param.name}
            onClick={() => handleInsertParam(param.name)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Typography variant="body2">{param.name}</Typography>
            {param.inTemplate && <Check size={16} color="primary" />}
          </MenuItem>
        ))}
      </Menu>

      {/* CodeMirror Editor */}
      <Box
        sx={{
          flex: 1,
          marginBottom: validationError ? 1 : 2,
          maxHeight: '500px',
          overflow: 'auto',
        }}
      >
        <CodeMirror
          value={content}
          maxHeight="400px"
          theme={theme.palette.mode === 'dark' ? 'dark' : 'light'}
          extensions={extensions}
          onChange={value => {
            // Only update if the value actually changed to prevent
            // state reset loops that cause typed characters to disappear
            if (value !== content) {
              onChange(value);
            }
          }}
          ref={editorRef}
          basicSetup={{
            lineNumbers: true,
            highlightActiveLineGutter: true,
            highlightSpecialChars: true,
            history: true,
            foldGutter: false,
            drawSelection: true,
            dropCursor: true,
            allowMultipleSelections: true,
            indentOnInput: true,
            syntaxHighlighting: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: false,
            rectangularSelection: true,
            crosshairCursor: true,
            highlightActiveLine: true,
            highlightSelectionMatches: true,
            closeBracketsKeymap: true,
            defaultKeymap: true,
            searchKeymap: true,
            historyKeymap: true,
            foldKeymap: false,
            completionKeymap: false,
            lintKeymap: false,
          }}
        />
      </Box>

      {/* Validation Error */}
      {validationError && (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          Invalid format: {validationError}.{' '}
          {onOpenSyntaxGuide && (
            <Button
              onClick={onOpenSyntaxGuide}
              sx={{
                textTransform: 'none',
                textDecoration: 'underline',
                color: 'inherit',
                fontWeight: 'bold',
                minWidth: 'auto',
                padding: 0,
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Syntax guide
            </Button>
          )}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Left side - AI improvement icon and Save/Reset buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Expand to fullscreen button */}
          <Tooltip title="Open in fullscreen" arrow>
            <IconButton
              onClick={handleOpenDialog}
              size="small"
              sx={{ color: 'primary.main' }}
            >
              <Maximize2 size={16} />
            </IconButton>
          </Tooltip>

          {/* Save and Reset Icons */}
          {isModified && (
            <>
              <IconButton
                onClick={onReset}
                size="small"
                title="Reset prompt"
                sx={{ color: 'secondary.main' }}
              >
                <RefreshCw size={16} />
              </IconButton>
              <IconButton
                onClick={onSave}
                size="small"
                title="Save prompt"
                disabled={!canSave}
                sx={{ color: 'success.main' }}
              >
                <Save size={16} />
              </IconButton>
            </>
          )}
        </Box>

        {/* Right side - Run button */}
        <Button
          variant="contained"
          color="primary"
          onClick={onRun}
          disabled={!canRun || isRunning}
          sx={{ minWidth: '120px' }}
        >
          {isRunning ? 'Running...' : 'Run'}
        </Button>
      </Box>

      {/* Fullscreen Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            handleDialogBackdropClick(
              event as React.MouseEvent<HTMLDivElement>
            );
          } else {
            handleCloseDialog();
          }
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '900px',
            height: 'calc(100vh - 64px)',
            maxHeight: 'calc(100vh - 64px)',
            margin: 'auto',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            padding: '16px 24px',
          }}
        >
          <Typography variant="h6">Edit Prompt</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Save and Reset Icons - only show if modified */}
            {isModified && (
              <>
                <IconButton
                  onClick={onReset}
                  size="small"
                  title="Reset prompt"
                  sx={{ color: 'secondary.main' }}
                >
                  <RefreshCw size={18} />
                </IconButton>
                <IconButton
                  onClick={onSave}
                  size="small"
                  title="Save prompt"
                  disabled={!canSave}
                  sx={{ color: 'success.main' }}
                >
                  <Save size={18} />
                </IconButton>
              </>
            )}
            {/* Close button */}
            <IconButton
              onClick={handleCloseDialog}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <X size={18} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          {/* Validation Error in Dialog */}
          {validationError && (
            <Alert severity="error" sx={{ margin: '16px 16px 0 16px' }}>
              Invalid format: {validationError}.{' '}
              {onOpenSyntaxGuide && (
                <Button
                  onClick={onOpenSyntaxGuide}
                  sx={{
                    textTransform: 'none',
                    textDecoration: 'underline',
                    color: 'inherit',
                    fontWeight: 'bold',
                    minWidth: 'auto',
                    padding: 0,
                    '&:hover': {
                      textDecoration: 'underline',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Syntax guide
                </Button>
              )}
            </Alert>
          )}

          {/* CodeMirror Editor in Dialog */}
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <CodeMirror
              value={content}
              height="100%"
              theme={theme.palette.mode === 'dark' ? 'dark' : 'light'}
              extensions={extensions}
              onChange={value => {
                if (value !== content) {
                  onChange(value);
                }
              }}
              ref={dialogEditorRef}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLineGutter: true,
                highlightSpecialChars: true,
                history: true,
                foldGutter: false,
                drawSelection: true,
                dropCursor: true,
                allowMultipleSelections: true,
                indentOnInput: true,
                syntaxHighlighting: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: false,
                rectangularSelection: true,
                crosshairCursor: true,
                highlightActiveLine: true,
                highlightSelectionMatches: true,
                closeBracketsKeymap: true,
                defaultKeymap: true,
                searchKeymap: true,
                historyKeymap: true,
                foldKeymap: false,
                completionKeymap: false,
                lintKeymap: false,
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
